/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { createSelector } from '@ngrx/store';
import { selectPaymentFeatureState } from '../reducers';
import {
  selectBookingBreakdown,
  selectBookingCreditFilePayments,
  selectBookingOtherFees,
  selectBookingSpoilageFeesTotalCost,
  selectBookingVoucherPayments,
  selectJourneys,
  selectOriginalBookingCreditFilePayments,
  selectOriginalBookingVoucherPayments,
  selectPassengersList,
} from './booking.selector';
import { Fee, Passenger } from '@models/booking.models';
import { selectExtrasWithPrices } from './extras.selector';
import { selectBagsTotalPriceTripSummary } from './bags.selector';
import { selectMealsTotalCostTripSummary } from './meals.selector';
import { selectSeatsTotalCostTripSummary } from './seat.selector';
import { UpliftTraveler, UpliftOrderInfo } from 'src/app/typings/uplift';
import { selectPaymentCopy } from './cms.selector';
import { CmsSubKeys } from '@constants/cms.constants';
import { selectAvailabilityTripTypeState } from './availability.selector';
import { convertToUpliftStringBirthDate, createAirReservation } from '@utils/payment.utils';
import { removePhoneNumberCountryCode } from '@utils/data-utils';

export const selectVoucherVerificationInformation = createSelector(
  selectPaymentFeatureState,
  (state) => state.voucherVerificationInformation,
);

export const selectMarketingOptIn = createSelector(
  selectPaymentFeatureState,
  (state) => state.marketingOptIn,
);

export const selectIsUsingMultiplePayments = createSelector(
  selectPaymentFeatureState,
  (state) => state.isUsingMultiplePayments,
);

// vouhcers on current booking in state but not on original booking cannot be
// submitted again (will disable any forms with these codes)
// NOTE: this will return one code for every voucher on the current booking
// if it passes the filter, so you may see duplicates.
export const selectAppliedUnsubmittedVoucherCodes = createSelector(
  selectBookingVoucherPayments,
  selectOriginalBookingVoucherPayments,
  (currentVouchers, originalVouchers) => {
    return currentVouchers
      .filter((v) => {
        const numberOfThisVoucherOnOriginalBooking = originalVouchers.filter(
          (originalVoucher) =>
            originalVoucher.details?.accountNumber === v.details?.accountNumber,
        ).length;
        const numberOfThisVoucherOnCurrentBooking = currentVouchers.filter(
          (currentVoucher) =>
            currentVoucher.details?.accountNumber === v.details?.accountNumber,
        ).length;
        // can only use voucher once per flow session
        return (
          numberOfThisVoucherOnCurrentBooking >
          numberOfThisVoucherOnOriginalBooking
        );
      })
      .map((v) => v.details?.accountNumber);
  },
);

export const selectCreditFileInformation = createSelector(
  selectPaymentFeatureState,
  (state) => state.creditFileInformation,
);

// credit file on current booking in state but not on original booking cannot be
// submitted again (will disable any forms with these codes)
// NOTE: this will return one code for every credit file on the current booking
// if it passes the filter, so you may see duplicates.
export const selectAppliedUnsubmittedCreditFileCodes = createSelector(
  selectBookingCreditFilePayments,
  selectOriginalBookingCreditFilePayments,
  (currentCreditFiles, originalCreditFiles) => {
    return currentCreditFiles
      .filter((v) => {
        const numberOfThisCreditFileOnOriginalBooking =
          originalCreditFiles.filter(
            (originalCreditFile) =>
              originalCreditFile.details?.accountNumber ===
              v.details?.accountNumber,
          ).length;
        const numberOfThisCreditFileOnCurrentBooking =
          currentCreditFiles.filter(
            (currentCreditFile) =>
              currentCreditFile.details?.accountNumber ===
              v.details?.accountNumber,
          ).length;
        // can only use credit file once per flow session
        return (
          numberOfThisCreditFileOnCurrentBooking >
          numberOfThisCreditFileOnOriginalBooking
        );
      })
      .map((v) => v.details?.accountNumber);
  },
);

export const selectMaxPaymentAmount = createSelector(
  selectPaymentFeatureState,
  (state) => {
    return state.maxPaymentAmount;
  },
);

const selectAddOnSemiBreakdown = createSelector(
  selectBagsTotalPriceTripSummary,
  selectSeatsTotalCostTripSummary,
  selectMealsTotalCostTripSummary,
  selectBookingSpoilageFeesTotalCost,
  selectExtrasWithPrices,
  selectBookingOtherFees,
  (bagsTotal, seatsTotal, mealsTotal, spoilageTotal, extrasWithPrices, otherFees ) => {

    let semiBreakdown: {price: number, code: string}[] = [];
    if (bagsTotal > 0) {
      semiBreakdown.push({ code: 'bags', price: bagsTotal });
    }
    if (seatsTotal > 0) {
      semiBreakdown.push({ code: 'seats', price: seatsTotal });
    }
    if (mealsTotal > 0) {
      semiBreakdown.push({ code: 'meals', price: mealsTotal });
    }
    if (spoilageTotal > 0) {
      semiBreakdown.push({ code: 'spoilage', price: spoilageTotal });
    }
    const totalExtrasWithPrices = Object.entries(extrasWithPrices.reduce((acc, curr) => {
      const { price, name } = curr;
      if (acc[name]) {
          acc[name] += price;
      } else {
          acc[name] = price;
      }
      return acc;
  }, {} as { [name: string]: number })).map(([name, price]) => ({ price, code: name }));

  semiBreakdown = semiBreakdown.concat(totalExtrasWithPrices);

    const otherFeesWithPrices: { price: number; code: string }[] = otherFees
    ?.flatMap(of => of.otherFees)
    ?.flatMap(f => f?.fees)
    ?.filter(f => f?.price ?? 0 > 0)
    .reduce((acc: { price: number; code: string }[], f: Fee) => {
        const existingFeeIndex = acc.findIndex(item => item.code === f.code);
        if (existingFeeIndex >= 0) {

            acc[existingFeeIndex]!.price += f.price;
        } else {
            acc.push({ price: f.price, code: f.code });
        }
        return acc;
    }, []) ?? [];

    semiBreakdown = semiBreakdown.concat(otherFeesWithPrices);

    return semiBreakdown;
  }
);

export const selectPaymentOrderInfo = createSelector(
  selectPassengersList,
  selectJourneys,
  selectAvailabilityTripTypeState,
  selectBookingBreakdown,
  selectBookingOtherFees,
  selectAddOnSemiBreakdown,
  selectPaymentCopy,
  (passengers, journeys, tripType, breakdown, otherFees, addOnSemiBreakdown, paymentCopy )=> {
    if(passengers && tripType && journeys && breakdown && paymentCopy) {
      const flightInformationCopy = paymentCopy[CmsSubKeys.flightInformation];
      const billingPassenger = passengers.filter(p => !!p.addresses && p.addresses.length > 0)[0]!;
      const billingAddress = billingPassenger.addresses![0]!;
      return {
        travelers: passengers.map((p: Passenger, idx: number) => {

          return { id: idx,
            date_of_birth: convertToUpliftStringBirthDate(p.info.dateOfBirth!),
            first_name: p.name?.first,
            last_name: p.name?.last

          } as UpliftTraveler
        }),
        billing_contact: {
          first_name: billingPassenger.name?.first,
          last_name: billingPassenger.name?.last,
          phone: removePhoneNumberCountryCode(billingAddress.phone),
          email: billingAddress.emailAddress,
          date_of_birth: convertToUpliftStringBirthDate(billingPassenger.info.dateOfBirth!),
          region: billingAddress.provinceState,
          country: billingAddress.countryCode,
          postal_code: billingAddress.postalCode
        },
        air_reservations: createAirReservation(journeys, tripType, otherFees, flightInformationCopy),
        add_ons: addOnSemiBreakdown.filter(s => s.code !== 'TINS').map((e: {price: number, code: string}, idx: number) => {
          return { id: idx + '_' + e.code, name: e.code, price: e.price * 100}
        }),
        order_amount: Math.round(breakdown.balanceDue * 100)
      } as UpliftOrderInfo
    }
    return null;
  }
);

export const selectPaymentFlow = createSelector(
  selectPaymentFeatureState,
  (state) => {
    return state.flow;
  },
);

export const selectPaymentReferenceStatus = createSelector(
  selectPaymentFeatureState,
  (state) => {
    return state.paymentStatusReference;
  },
);

export const selectPaymentInProgress = createSelector(
  selectPaymentFeatureState,
  (state) => {
    return state.paymentInProgress;
  },
);

export const selectCreditCardFees = createSelector(
  selectPaymentFeatureState,
  (state) => {
    return state.creditCardFees;
  },
);

export const selectCreditCardFeeAmountApplied = createSelector(
  selectPaymentFeatureState,
  (state) => {
    return state.creditCardFeeAmountApplied;
  },
);

export const selectIsCorporateCreditCard = createSelector(
  selectPaymentFeatureState,
  (state) => {
    return state.isCorporateCreditCard;
  },
);
